// /src/components/admin/ContentModerationTab.jsx
// Content Moderation - Hide or restore posts and comments without deleting them
// Includes Problem Reports view

import { useState, useEffect } from 'react';
import { useTheme } from '../../themes/ThemeProvider';
import { supabaseFetch } from '../../lib/supabase';
import { RefreshCw, Search, Copy, Check, Calendar, MapPin } from 'lucide-react';
import { useTranslation } from '../../i18n/useTranslation';
import { logAdminAction, auditFailureMessage } from '../../utils/adminAuditLog';
import ConfirmationModal from '../core/ConfirmationModal';
import useMediaQuery from '../../hooks/useMediaQuery';
import { formatTextWithStyles } from '../../utils/formatText';
import { tokens } from '../../styles/designTokens';
import InvitationReportsSection from './InvitationReportsSection';

const REASON_LABELS = {
  // Current user-facing categories (ReportModal)
  offensive:             'Offensive or abusive',
  spam:                  'Spam',
  harassment:            'Harassment',
  off_topic:             'Off-topic',
  // Legacy categories (older reports)
  misinformation:        'Misinformation',
  hate_speech:           'Hate speech',
  violence:              'Violence',
  inappropriate_content: 'Inappropriate content',
  wrong_locality:        'Wrong locality',
  copyright:             'Copyright',
  other:                 'Other',
};

const IMAGE_SIZES = { S: '33%', M: '66%', L: '100%' };

const renderArticleContent = (content, inlineImages = []) => {
  if (!content) return null;
  const styleBase = { color: '#374151', lineHeight: '1.625', marginBottom: '16px' };
  const hasMarkers = /\[(IMAGE|IMG):[^\]]+\]/.test(content);
  if (!hasMarkers) {
    return formatTextWithStyles(content, 'ac-', styleBase);
  }
  const parts = content.split(/\[(?:IMAGE|IMG):([^\]]+)\]/);
  return parts.map((part, index) => {
    if (index % 2 === 0) {
      if (!part.trim()) return null;
      return formatTextWithStyles(part, `ac-${index}-`, styleBase);
    }
    const image = inlineImages.find(img => img.id === part);
    if (image) {
      const width = IMAGE_SIZES[image.size] || '100%';
      return (
        <div key={`img-${index}`} style={{ margin: '16px 0' }}>
          <img src={image.displayUrl} alt="" loading="lazy" style={{ width, maxWidth: '100%', height: 'auto', borderRadius: '8px' }} />
        </div>
      );
    }
    return null;
  });
};

const getVideoEmbed = (url) => {
  if (!url) return null;
  if (url.includes('youtube.com') || url.includes('youtu.be')) {
    let videoId;
    if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1].split('?')[0].split('&')[0];
    } else if (url.includes('youtube.com')) {
      const urlParams = new URLSearchParams(url.split('?')[1]);
      videoId = urlParams.get('v');
    }
    if (videoId) return `https://www.youtube-nocookie.com/embed/${videoId}`;
  }
  if (url.includes('vimeo.com')) {
    const match = url.match(/vimeo\.com\/(\d+)/);
    if (match && match[1]) return `https://player.vimeo.com/video/${match[1]}`;
  }
  if (url.includes('framatube.org') || url.includes('peertube')) {
    try {
      const urlObj = new URL(url);
      const origin = urlObj.origin;
      let videoPath;
      if (url.includes('/w/')) videoPath = url.split('/w/')[1].split('?')[0];
      else if (url.includes('/videos/watch/')) videoPath = url.split('/videos/watch/')[1].split('?')[0];
      if (videoPath) return `${origin}/videos/embed/${videoPath}`;
    } catch (e) { /* unrecognized */ }
  }
  return null;
};

const ContentModerationTab = () => {
  const theme = useTheme();
  const { windowWidth } = useMediaQuery();
  const { t, formattingLocale } = useTranslation('admin');
  const [activeSubTab, setActiveSubTab] = useState('articles');
  const [invitationReportsReload, setInvitationReportsReload] = useState(0); // Invitation reports sub-tab (#54)
  const [loading, setLoading] = useState(true);
  const [articles, setArticles] = useState([]);
  const [comments, setComments] = useState([]);
  const [problemReports, setProblemReports] = useState([]);
  const [contentReports, setContentReports] = useState([]);
  const [pendingContentReportCount, setPendingContentReportCount] = useState(0);
  const [chatReports, setChatReports] = useState([]);
  const [pendingChatReportCount, setPendingChatReportCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const [previewArticle, setPreviewArticle] = useState(null);
  const [infoModal, setInfoModal] = useState({ visible: false, message: '' });
  const showInfo = (message) => setInfoModal({ visible: true, message });
  // The action succeeded; a failed audit write is shown after the reload, never swallowed.
  const showAuditFailure = (audit) => {
    const message = auditFailureMessage(t, audit);
    if (message) showInfo(message);
  };

  const [reasonModal, setReasonModal] = useState({ visible: false, title: '', subtitle: '', placeholder: '', onConfirm: null });
  const [reasonText, setReasonText] = useState('');
  const [reasonSubmitting, setReasonSubmitting] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ visible: false, title: '', message: '', onOk: null });

  const openReasonModal = (title, subtitle, placeholder, onConfirm) => {
    setReasonText('');
    setReasonModal({ visible: true, title, subtitle, placeholder, onConfirm });
  };
  const closeReasonModal = () => setReasonModal({ visible: false, title: '', subtitle: '', placeholder: '', onConfirm: null });
  const closeConfirmModal = () => setConfirmModal({ visible: false, title: '', message: '', onOk: null });

  const loadArticles = async () => {
    setLoading(true);
    try {
      // The embedded author select is intentional and safe here: supabaseFetch issues a native
      // fetch to PostgREST and passes `select` through verbatim, so the Supabase-JS-client hang
      // that Architecture.md warns about has no mechanism on this path (DATABASE.md states the
      // rule with that scope intact).
      // The newest 100, plus every platform-wide post: none of the 21 tutorials is among the
      // newest 100, and this list is where platform content is made or unmade (addendum 4).
      const select = 'id,title,created_at,is_published,is_platform_content,author:users(display_name)';
      const [newest, platform] = await Promise.all([
        supabaseFetch.query('articles', { select, order: 'created_at.desc', limit: 100 }),
        supabaseFetch.query('articles', { select, eq: { is_platform_content: true }, order: 'created_at.desc' })
      ]);

      if (newest.error || platform.error) {
        console.error('Error loading articles:', newest.error || platform.error);
        return;
      }

      const byId = new Map();
      [...(newest.data || []), ...(platform.data || [])].forEach(a => byId.set(a.id, a));
      setArticles([...byId.values()].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
    } catch (err) {
      console.error('Error loading articles:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadComments = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabaseFetch.query('chats', {
        select: 'id,content,created_at,is_hidden,author:users(display_name),article:articles(title)',
        order: 'created_at.desc',
        limit: 100
      });

      if (error) {
        console.error('Error loading comments:', error);
        return;
      }

      setComments(data || []);
    } catch (err) {
      console.error('Error loading comments:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadProblemReports = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabaseFetch.query('error_reports', {
        select: '*',
        eq: { status: 'new' },
        order: 'created_at.desc',
        limit: 50
      });

      if (error) {
        console.error('Error loading problem reports:', error);
        return;
      }

      setProblemReports(data || []);
    } catch (err) {
      console.error('Error loading problem reports:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadContentReports = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabaseFetch.query('content_reports', {
        select: 'id,article_id,target_type,reason,category,details,status,created_at,article:articles!article_id(title,summary,content,cover_image_url,video_url,inline_images,created_at,article_type,author:users(display_name)),reporter:users!reporter_id(display_name)',
        order: 'created_at.desc',
        limit: 100
      });

      if (error) {
        console.error('Error loading content reports:', error);
        return;
      }

      // Exclude chat/comment reports — those appear in the Chat/Comment Reports tab
      const rows = (data || []).filter(r => !r.target_type || r.target_type === 'article' || r.target_type === 'event');
      setContentReports(rows);
      setPendingContentReportCount(rows.filter(r => r.status === 'pending').length);
    } catch (err) {
      console.error('Error loading content reports:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadChatReports = async () => {
    setLoading(true);
    try {
      // Step 1: load all chat/comment reports
      const { data, error } = await supabaseFetch.query('content_reports', {
        select: 'id,target_id,category,details,status,created_at,reporter:users!reporter_id(display_name)',
        eq: { target_type: 'chat' },
        order: 'created_at.desc',
        limit: 100
      });

      if (error) {
        console.error('Error loading chat reports:', error);
        return;
      }

      const rows = data || [];

      // Step 2: batch-fetch the referenced chat rows to get content + article title
      const chatIds = [...new Set(rows.map(r => r.target_id).filter(Boolean))];
      let chatMap = {};
      if (chatIds.length > 0) {
        const { data: chatData } = await supabaseFetch.query('chats', {
          select: 'id,content,article:articles!article_id(title)',
          inFilter: { id: chatIds }
        });
        (chatData || []).forEach(c => { chatMap[c.id] = c; });
      }

      const enriched = rows.map(r => ({
        ...r,
        chat: chatMap[r.target_id] || null
      }));

      setChatReports(enriched);
      setPendingChatReportCount(enriched.filter(r => r.status === 'pending').length);
    } catch (err) {
      console.error('Error loading chat reports:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeSubTab === 'articles') loadArticles();
    else if (activeSubTab === 'comments') loadComments();
    else if (activeSubTab === 'reports') loadProblemReports();
    else if (activeSubTab === 'contentReports') loadContentReports();
    else if (activeSubTab === 'chatReports') loadChatReports();
  }, [activeSubTab]);

  // Fetch event_details when previewing an event article
  useEffect(() => {
    if (!previewArticle || previewArticle.article_type !== 'event' || previewArticle._eventDetailsFetched) return;
    supabaseFetch.query('event_details', {
      select: 'start_date,end_date,venue_name,venue_address,venue_postcode',
      eq: { article_id: previewArticle.id },
      single: true
    }).then(({ data }) => {
      setPreviewArticle(prev => prev ? ({ ...prev, ...(data || {}), _eventDetailsFetched: true }) : null);
    });
  }, [previewArticle?.id]);

  // Load pending report counts on mount for badges
  useEffect(() => {
    supabaseFetch.query('content_reports', {
      select: 'id,status,target_type',
      eq: { status: 'pending' },
      limit: 200
    }).then(({ data }) => {
      const rows = data || [];
      setPendingContentReportCount(rows.filter(r => !r.target_type || r.target_type === 'article' || r.target_type === 'event').length);
      setPendingChatReportCount(rows.filter(r => r.target_type === 'chat' || r.target_type === 'comment').length);
    }).catch(() => {});
  }, []);

  const handleToggleArticle = (articleId, currentStatus, articleTitle) => {
    if (currentStatus) {
      // Hiding an article — requires reason
      openReasonModal(
        t('hide') + ' ' + t('article'),
        articleTitle,
        t('reasonHideArticle') || 'Enter reason for hiding this article',
        async (reason) => {
          try {
            const { error } = await supabaseFetch.rpc('admin_set_article_published', {
              p_article_id: articleId,
              p_is_published: false
            });
            if (error) { showInfo('Failed to hide article: ' + (error.message || JSON.stringify(error))); return; }
            const audit = await logAdminAction({ actionType: 'article_unpublish', targetType: 'article', targetId: articleId, reason });
            loadArticles();
            showAuditFailure(audit);
          } catch (err) {
            console.error('Error hiding article:', err);
            showInfo(t('failedToToggleArticle'));
          }
        }
      );
    } else {
      // Restoring an article — confirmation only (no reason required)
      setConfirmModal({
        visible: true,
        title: t('restore') + ' ' + t('article'),
        message: articleTitle,
        onOk: async () => {
          try {
            const { error } = await supabaseFetch.rpc('admin_set_article_published', {
              p_article_id: articleId,
              p_is_published: true
            });
            if (error) { showInfo('Failed to restore article: ' + (error.message || JSON.stringify(error))); return; }
            const audit = await logAdminAction({ actionType: 'article_publish', targetType: 'article', targetId: articleId });
            loadArticles();
            showAuditFailure(audit);
          } catch (err) {
            console.error('Error restoring article:', err);
            showInfo(t('failedToToggleArticle'));
          }
          closeConfirmModal();
        }
      });
    }
  };

  // Only here is a post made or unmade platform-wide (addendum 4; migration 20260928f guards the
  // column against every other path).
  const handleTogglePlatformContent = (articleId, isPlatform, articleTitle) => {
    setConfirmModal({
      visible: true,
      title: isPlatform ? t('unmakePlatformWide') : t('makePlatformWide'),
      message: isPlatform
        ? t('confirmUnmakePlatformWide', { title: articleTitle })
        : t('confirmMakePlatformWide', { title: articleTitle }),
      onOk: async () => {
        try {
          const { error } = await supabaseFetch.rpc('admin_set_article_platform_content', {
            p_article_id: articleId,
            p_is_platform: !isPlatform
          });
          if (error) {
            console.error('Error changing platform content:', error);
            // A post with no group cannot be placed again when it stops being platform-wide
            // (admin_set_article_platform_content, addendum 5 ruling 4).
            showInfo(String(error.message || '').includes('platform_unmake_needs_group')
              ? t('platformUnmakeNeedsGroup')
              : t('failedToChangePlatformWide'));
          } else {
            const audit = await logAdminAction({
              actionType: isPlatform ? 'article_unmake_platform' : 'article_make_platform',
              targetType: 'article',
              targetId: articleId
            });
            loadArticles();
            showAuditFailure(audit);
          }
        } catch (err) {
          console.error('Error changing platform content:', err);
          showInfo(t('failedToChangePlatformWide'));
        }
        closeConfirmModal();
      }
    });
  };

  const handleToggleComment = (commentId, isHidden, commentExcerpt) => {
    if (!isHidden) {
      // Hiding a comment — requires reason
      openReasonModal(
        t('hide') + ' ' + t('comment'),
        commentExcerpt,
        t('reasonHideComment') || 'Enter reason for hiding this comment',
        async (reason) => {
          try {
            const { error } = await supabaseFetch.rpc('admin_set_chat_hidden', {
              p_chat_id: commentId,
              p_is_hidden: true
            });
            if (error) { showInfo(t('failedToToggleComment')); return; }
            const audit = await logAdminAction({ actionType: 'comment_hide', targetType: 'comment', targetId: commentId, reason });
            loadComments();
            showAuditFailure(audit);
          } catch (err) {
            console.error('Error hiding comment:', err);
            showInfo(t('failedToToggleComment'));
          }
        }
      );
    } else {
      // Restoring a comment — confirmation only
      setConfirmModal({
        visible: true,
        title: t('restore') + ' ' + t('comment'),
        message: commentExcerpt,
        onOk: async () => {
          try {
            const { error } = await supabaseFetch.rpc('admin_set_chat_hidden', {
              p_chat_id: commentId,
              p_is_hidden: false
            });
            if (error) { showInfo(t('failedToToggleComment')); return; }
            const audit = await logAdminAction({ actionType: 'comment_unhide', targetType: 'comment', targetId: commentId });
            loadComments();
            showAuditFailure(audit);
          } catch (err) {
            console.error('Error restoring comment:', err);
            showInfo(t('failedToToggleComment'));
          }
          closeConfirmModal();
        }
      });
    }
  };

  const handleUpdateContentReport = (reportId, newStatus, subtitle) => {
    openReasonModal(
      newStatus === 'actioned' ? t('action') + ' ' + t('report') : t('dismiss') + ' ' + t('report'),
      subtitle || '',
      newStatus === 'actioned'
        ? (t('reasonActionReport') || 'Enter reason for actioning this report')
        : (t('reasonDismissReport') || 'Enter reason for dismissing this report'),
      async (reason) => {
        try {
          const { error } = await supabaseFetch.update('content_reports', { status: newStatus, reviewed_at: new Date().toISOString() }, { eq: { id: reportId } });
          if (error) { showInfo(t('failedToUpdateReport')); return; }
          const audit = await logAdminAction({ actionType: `content_report_${newStatus}`, targetType: 'content_report', targetId: reportId, reason });
          loadContentReports();
          showAuditFailure(audit);
        } catch (err) {
          console.error('Error updating content report:', err);
          showInfo(t('failedToUpdateReport'));
        }
      }
    );
  };

  const handleUpdateChatReport = (reportId, newStatus, subtitle) => {
    openReasonModal(
      newStatus === 'actioned' ? t('action') + ' ' + t('report') : t('dismiss') + ' ' + t('report'),
      subtitle || '',
      newStatus === 'actioned'
        ? (t('reasonActionReport') || 'Enter reason for actioning this report')
        : (t('reasonDismissReport') || 'Enter reason for dismissing this report'),
      async (reason) => {
        try {
          const { error } = await supabaseFetch.update('content_reports', { status: newStatus, reviewed_at: new Date().toISOString() }, { eq: { id: reportId } });
          if (error) { showInfo(t('failedToUpdateReport')); return; }
          // A chat report is a content_reports row like any other: the audit vocabulary has no
          // chat_report_* values, so it logs as content_report_<status> with the report's kind alongside.
          const audit = await logAdminAction({
            actionType: `content_report_${newStatus}`,
            targetType: 'content_report',
            targetId: reportId,
            reason,
            metadata: { report_target_type: 'chat' }
          });
          loadChatReports();
          showAuditFailure(audit);
        } catch (err) {
          console.error('Error updating chat report:', err);
          showInfo(t('failedToUpdateReport'));
        }
      }
    );
  };

  const handleMarkReportResolved = (reportId, reportTitle) => {
    openReasonModal(
      t('markResolved'),
      reportTitle || t('userReport'),
      t('reasonResolveReport') || 'Enter reason for marking this report as resolved',
      async (reason) => {
        try {
          const { error } = await supabaseFetch.update('error_reports', { status: 'resolved' }, { eq: { id: reportId } });
          if (error) { showInfo(t('failedToMarkResolved')); return; }
          const audit = await logAdminAction({ actionType: 'problem_report_resolved', targetType: 'error_report', targetId: reportId, reason });
          loadProblemReports();
          showAuditFailure(audit);
        } catch (err) {
          console.error('Error marking report resolved:', err);
          showInfo(t('failedToMarkResolved'));
        }
      }
    );
  };

  const copyReportToClipboard = (report) => {
    const formattedText = formatReportForNextcloud(report);
    navigator.clipboard.writeText(formattedText).then(() => {
      setCopiedId(report.id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  // Markdown for the developers' Nextcloud board: English, date included, whichever language the
  // admin copying it uses (#85 — the one fixed en-GB left in this file, like IssuesDropdown's card).
  const formatReportForNextcloud = (report) => {
    const date = new Date(report.created_at).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

    const reportType = (report.report_type || 'bug').toUpperCase();

    let text = `**[${reportType}] ${report.title || 'User Report'}**\n`;
    text += `**Date:** ${date}\n`;
    text += `**User Email:** ${report.user_email || 'Anonymous'}\n\n`;

    if (report.description) {
      text += `**Description:**\n${report.description}\n\n`;
    }

    // Parse debug context if available
    if (report.debug_context) {
      try {
        const debug = typeof report.debug_context === 'string'
          ? JSON.parse(report.debug_context)
          : report.debug_context;

        if (debug.component) text += `**Component:** ${debug.component}\n`;
        if (debug.selectedArea) text += `**Selected Area:** ${debug.selectedArea}\n`;
        if (debug.pathname) text += `**Path:** ${debug.pathname}\n`;

        if (debug.recentErrors?.length) {
          text += `\n**JS Errors (${debug.recentErrors.length}):**\n`;
          debug.recentErrors.forEach(err => {
            text += `  - ${err.message} (${err.source || ''}:${err.line || ''})\n`;
          });
        }
      } catch (e) {
        console.error('Error parsing debug context:', e);
      }
    }

    return text;
  };

  const filteredArticles = searchTerm
    ? articles.filter(a => a.title?.toLowerCase().includes(searchTerm.toLowerCase()))
    : articles;

  const filteredComments = searchTerm
    ? comments.filter(c => c.content?.toLowerCase().includes(searchTerm.toLowerCase()))
    : comments;

  const styles = {
    card: {
      backgroundColor: 'white',
      borderRadius: '8px',
      padding: '20px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px',
      flexWrap: 'wrap',
      gap: '12px'
    },
    title: {
      fontSize: '18px',
      fontWeight: 'bold',
      color: theme?.text?.primary || '#1A1A1A'
    },
    subTabBar: {
      display: 'flex',
      gap: '8px',
      marginBottom: '20px',
      borderBottom: `2px solid ${theme?.border || '#DDD'}`,
      paddingBottom: '8px'
    },
    subTab: (isActive) => ({
      padding: '8px 16px',
      border: 'none',
      background: 'none',
      borderBottom: isActive ? `2px solid ${theme?.primary || '#024B99'}` : '2px solid transparent',
      color: isActive ? (theme?.primary || '#024B99') : (theme?.text?.secondary || '#666'),
      fontWeight: isActive ? 'bold' : '500',
      fontSize: '13px',
      cursor: 'pointer',
      marginBottom: '-10px'
    }),
    searchBox: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      flex: 1,
      maxWidth: '400px'
    },
    searchInput: {
      flex: 1,
      padding: '8px 12px',
      borderRadius: '6px',
      border: `1px solid ${theme?.border || '#DDD'}`,
      fontSize: '13px'
    },
    refreshButton: {
      padding: '8px 16px',
      borderRadius: '6px',
      border: 'none',
      backgroundColor: theme?.primary || '#024B99',
      color: 'white',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      fontSize: '13px',
      fontWeight: '500'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: '13px'
    },
    th: {
      textAlign: 'left',
      padding: '12px 8px',
      borderBottom: `2px solid ${theme?.border || '#DDD'}`,
      color: theme?.text?.secondary || '#666',
      fontWeight: '600'
    },
    td: {
      padding: '12px 8px',
      borderBottom: `1px solid ${theme?.border || '#DDD'}`,
      color: theme?.text?.primary || '#1A1A1A'
    },
    actionButton: {
      padding: '4px 10px',
      borderRadius: '4px',
      border: 'none',
      fontSize: '12px',
      cursor: 'pointer',
      fontWeight: '500'
    },
    platformButton: {
      marginLeft: '6px',
      backgroundColor: tokens.colors.disabledBackground,
      color: tokens.colors.textSecondary
    },
    platformBadge: {
      marginLeft: '6px',
      backgroundColor: tokens.colors.disabledBackground,
      color: tokens.colors.textSecondary
    },
    hideButton: (isHidden) => ({
      backgroundColor: isHidden ? '#dcfce7' : '#fee2e2',
      color: isHidden ? '#15803d' : '#991b1b'
    }),
    statusBadge: (isVisible) => ({
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: '4px',
      fontSize: '11px',
      fontWeight: '500',
      backgroundColor: isVisible ? '#dcfce7' : '#fee2e2',
      color: isVisible ? '#15803d' : '#991b1b'
    }),
    reportCard: {
      backgroundColor: 'white',
      border: `1px solid ${theme?.border || '#DDD'}`,
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '12px'
    },
    reportHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '8px'
    },
    reportTitle: {
      fontSize: '15px',
      fontWeight: 'bold',
      color: theme?.text?.primary || '#1A1A1A'
    },
    reportBadge: {
      display: 'inline-block',
      padding: '3px 10px',
      borderRadius: '4px',
      fontSize: '12px',
      fontWeight: '500',
      backgroundColor: '#fee2e2',
      color: '#991b1b',
      marginBottom: '8px'
    },
    reportContent: {
      fontSize: '13px',
      color: theme?.text?.secondary || '#666',
      lineHeight: '1.5',
      marginTop: '8px',
      whiteSpace: 'pre-wrap'
    },
    reportActions: {
      display: 'flex',
      gap: '8px',
      marginTop: '12px'
    },
    copyButton: (copied) => ({
      padding: '6px 12px',
      borderRadius: '4px',
      border: 'none',
      backgroundColor: copied ? '#dcfce7' : theme?.primary || '#024B99',
      color: copied ? '#15803d' : 'white',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      fontSize: '12px'
    }),
    resolveButton: {
      padding: '6px 12px',
      borderRadius: '4px',
      border: 'none',
      backgroundColor: '#dcfce7',
      color: '#15803d',
      cursor: 'pointer',
      fontSize: '12px'
    },
    loadingBox: {
      textAlign: 'center',
      padding: '40px',
      color: theme?.text?.secondary || '#666'
    }
  };

  const renderArticles = () => (
    <>
      {loading ? (
        <div style={styles.loadingBox}>{t('loadingArticles')}</div>
      ) : filteredArticles.length === 0 ? (
        <div style={styles.loadingBox}>{t('noArticlesFound')}</div>
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>{t('title')}</th>
              <th style={styles.th}>{t('author')}</th>
              <th style={styles.th}>{t('publishedDate')}</th>
              <th style={styles.th}>{t('status')}</th>
              <th style={styles.th}>{t('actions')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredArticles.map(article => (
              <tr key={article.id}>
                <td style={styles.td}>{article.title}</td>
                <td style={styles.td}>{article.author?.display_name || t('unknown')}</td>
                <td style={styles.td}>{new Date(article.created_at).toLocaleDateString(formattingLocale)}</td>
                <td style={styles.td}>
                  <span style={styles.statusBadge(article.is_published !== false)}>
                    {article.is_published !== false ? t('published') : t('hidden')}
                  </span>
                  {article.is_platform_content && (
                    <span style={{ ...styles.statusBadge(true), ...styles.platformBadge }}>{t('platformWide')}</span>
                  )}
                </td>
                <td style={styles.td}>
                  <button
                    onClick={() => handleToggleArticle(article.id, article.is_published !== false, article.title)}
                    style={{ ...styles.actionButton, ...styles.hideButton(article.is_published === false) }}
                  >
                    {article.is_published !== false ? t('hide') : t('restore')}
                  </button>
                  <button
                    onClick={() => handleTogglePlatformContent(article.id, article.is_platform_content === true, article.title)}
                    style={{ ...styles.actionButton, ...styles.platformButton }}
                  >
                    {article.is_platform_content ? t('unmakePlatformWide') : t('makePlatformWide')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );

  const renderComments = () => (
    <>
      {loading ? (
        <div style={styles.loadingBox}>{t('loadingComments')}</div>
      ) : filteredComments.length === 0 ? (
        <div style={styles.loadingBox}>{t('noCommentsFound')}</div>
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>{t('content')}</th>
              <th style={styles.th}>{t('author')}</th>
              <th style={styles.th}>{t('article')}</th>
              <th style={styles.th}>{t('date')}</th>
              <th style={styles.th}>{t('status')}</th>
              <th style={styles.th}>{t('actions')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredComments.map(comment => (
              <tr key={comment.id}>
                <td style={styles.td}>
                  {comment.content?.substring(0, 50)}
                  {comment.content?.length > 50 ? '...' : ''}
                </td>
                <td style={styles.td}>{comment.author?.display_name || t('unknown')}</td>
                <td style={styles.td}>{comment.article?.title || 'N/A'}</td>
                <td style={styles.td}>{new Date(comment.created_at).toLocaleDateString(formattingLocale)}</td>
                <td style={styles.td}>
                  <span style={styles.statusBadge(!comment.is_hidden)}>
                    {comment.is_hidden ? t('hidden') : t('visible')}
                  </span>
                </td>
                <td style={styles.td}>
                  <button
                    onClick={() => handleToggleComment(comment.id, comment.is_hidden, (comment.content || '').substring(0, 60))}
                    style={{ ...styles.actionButton, ...styles.hideButton(comment.is_hidden) }}
                  >
                    {comment.is_hidden ? t('restore') : t('hide')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );

  const renderChatReports = () => (
    <>
      {loading ? (
        <div style={styles.loadingBox}>{t('loadingArticleReports')}</div>
      ) : chatReports.length === 0 ? (
        <div style={styles.loadingBox}>{t('noChatReports') || 'No chat/comment reports found.'}</div>
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>{t('content')}</th>
              <th style={styles.th}>{t('article')}/{t('chat') || 'Chat'}</th>
              <th style={styles.th}>{t('reason')}</th>
              <th style={styles.th}>{t('reporter')}</th>
              <th style={styles.th}>{t('date')}</th>
              <th style={styles.th}>{t('details')}</th>
              <th style={styles.th}>{t('status')}</th>
              <th style={styles.th}>{t('actions')}</th>
            </tr>
          </thead>
          <tbody>
            {chatReports.map(report => (
              <tr key={report.id}>
                <td style={{ ...styles.td, maxWidth: '180px', fontSize: '12px' }}>
                  {report.chat?.content
                    ? (report.chat.content.length > 60
                        ? report.chat.content.substring(0, 60) + '…'
                        : report.chat.content)
                    : <em style={{ color: '#9CA3AF' }}>{t('commentDeleted') || 'Deleted'}</em>}
                </td>
                <td style={{ ...styles.td, maxWidth: '140px', fontSize: '12px' }}>
                  {report.chat?.article?.title
                    ? (report.chat.article.title.length > 35
                        ? report.chat.article.title.substring(0, 35) + '…'
                        : report.chat.article.title)
                    : report.chat?.content
                    ? (report.chat.content.length > 35
                        ? report.chat.content.substring(0, 35) + '…'
                        : report.chat.content)
                    : <em style={{ color: '#9CA3AF' }}>—</em>}
                </td>
                <td style={styles.td}>
                  <span style={{
                    display: 'inline-block',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: '600',
                    backgroundColor: '#FEE2E2',
                    color: '#991B1B'
                  }}>
                    {REASON_LABELS[report.category] || report.category}
                  </span>
                </td>
                <td style={styles.td}>{report.reporter?.display_name || '—'}</td>
                <td style={styles.td}>{new Date(report.created_at).toLocaleDateString(formattingLocale)}</td>
                <td style={{ ...styles.td, maxWidth: '160px', fontSize: '12px', color: '#6B7280' }}>
                  {report.details || '—'}
                </td>
                <td style={styles.td}>
                  <span style={{
                    display: 'inline-block',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: '500',
                    ...(STATUS_BADGE_STYLE[report.status] || {})
                  }}>
                    {report.status}
                  </span>
                </td>
                <td style={styles.td}>
                  {report.status === 'pending' || report.status === 'reviewed' ? (
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button
                        onClick={() => handleUpdateChatReport(report.id, 'actioned', report.chat?.content ? report.chat.content.substring(0, 60) : t('commentDeleted') || 'Deleted')}
                        style={{
                          ...styles.actionButton,
                          backgroundColor: '#FEE2E2',
                          color: '#991B1B'
                        }}
                        title={t('actionTooltip')}
                      >
                        {t('action')}
                      </button>
                      <button
                        onClick={() => handleUpdateChatReport(report.id, 'dismissed', report.chat?.content ? report.chat.content.substring(0, 60) : t('commentDeleted') || 'Deleted')}
                        style={{
                          ...styles.actionButton,
                          backgroundColor: '#F3F4F6',
                          color: '#6B7280'
                        }}
                      >
                        {t('dismiss')}
                      </button>
                    </div>
                  ) : (
                    <span style={{ fontSize: '11px', color: '#9CA3AF' }}>{t('done')}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );

  const renderProblemReports = () => (
    <>
      {loading ? (
        <div style={styles.loadingBox}>{t('loadingProblemReports')}</div>
      ) : problemReports.length === 0 ? (
        <div style={styles.loadingBox}>{t('noUnresolvedReports')}</div>
      ) : (
        problemReports.map(report => (
          <div key={report.id} style={styles.reportCard}>
            <div style={styles.reportHeader}>
              <div>
                <div style={styles.reportTitle}>{report.title || t('userReport')}</div>
                <span style={styles.reportBadge}>{report.report_type?.toUpperCase() || 'BUG'}</span>
              </div>
            </div>
            <div style={{ fontSize: '12px', color: theme?.text?.secondary || '#666', marginBottom: '8px' }}>
              {new Date(report.created_at).toLocaleString(formattingLocale)} • {report.user_email || t('anonymous')}
            </div>
            {report.description && (
              <div style={styles.reportContent}>{report.description}</div>
            )}
            <div style={styles.reportActions}>
              <button
                onClick={() => copyReportToClipboard(report)}
                style={styles.copyButton(copiedId === report.id)}
              >
                {copiedId === report.id ? (
                  <>
                    <Check size={12} />
                    {t('copied')}
                  </>
                ) : (
                  <>
                    <Copy size={12} />
                    {t('copyToNextcloud')}
                  </>
                )}
              </button>
              <button
                onClick={() => handleMarkReportResolved(report.id, report.title)}
                style={styles.resolveButton}
              >
                {t('markResolved')}
              </button>
            </div>
          </div>
        ))
      )}
    </>
  );

  const STATUS_BADGE_STYLE = {
    pending:   { backgroundColor: '#FEF3C7', color: '#92400E' },
    reviewed:  { backgroundColor: '#DBEAFE', color: '#1E40AF' },
    actioned:  { backgroundColor: '#DCFCE7', color: '#15803D' },
    dismissed: { backgroundColor: '#F3F4F6', color: '#6B7280' },
  };

  const renderContentReports = () => (
    <>
      {loading ? (
        <div style={styles.loadingBox}>{t('loadingArticleReports')}</div>
      ) : contentReports.length === 0 ? (
        <div style={styles.loadingBox}>{t('noArticleReports')}</div>
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>{t('article')}</th>
              <th style={styles.th}>{t('reason')}</th>
              <th style={styles.th}>{t('reporter')}</th>
              <th style={styles.th}>{t('date')}</th>
              <th style={styles.th}>{t('details')}</th>
              <th style={styles.th}>{t('status')}</th>
              <th style={styles.th}>{t('actions')}</th>
            </tr>
          </thead>
          <tbody>
            {contentReports.map(report => (
              <tr key={report.id}>
                <td style={styles.td}>
                  {report.article?.title
                    ? (report.article.title.length > 40
                        ? report.article.title.substring(0, 40) + '…'
                        : report.article.title)
                    : <em style={{ color: '#9CA3AF' }}>{t('articleDeleted')}</em>}
                </td>
                <td style={styles.td}>
                  <span style={{
                    display: 'inline-block',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: '600',
                    backgroundColor: '#FEE2E2',
                    color: '#991B1B'
                  }}>
                    {REASON_LABELS[report.reason] || REASON_LABELS[report.category] || report.category || report.reason}
                  </span>
                </td>
                <td style={styles.td}>{report.reporter?.display_name || '—'}</td>
                <td style={styles.td}>{new Date(report.created_at).toLocaleDateString(formattingLocale)}</td>
                <td style={{ ...styles.td, maxWidth: '160px', fontSize: '12px', color: '#6B7280' }}>
                  {report.details || '—'}
                </td>
                <td style={styles.td}>
                  <span style={{
                    display: 'inline-block',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: '500',
                    ...(STATUS_BADGE_STYLE[report.status] || {})
                  }}>
                    {report.status}
                  </span>
                </td>
                <td style={styles.td}>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {report.article && (
                      <button
                        onClick={() => setPreviewArticle(report.article)}
                        style={{
                          ...styles.actionButton,
                          backgroundColor: '#DBEAFE',
                          color: '#1E40AF'
                        }}
                      >
                        View
                      </button>
                    )}
                  {report.status === 'pending' || report.status === 'reviewed' ? (
                    <>
                      <button
                        onClick={() => handleUpdateContentReport(report.id, 'actioned', report.article?.title || t('articleDeleted'))}
                        style={{
                          ...styles.actionButton,
                          backgroundColor: '#FEE2E2',
                          color: '#991B1B'
                        }}
                        title={t('actionTooltip')}
                      >
                        {t('action')}
                      </button>
                      <button
                        onClick={() => handleUpdateContentReport(report.id, 'dismissed', report.article?.title || t('articleDeleted'))}
                        style={{
                          ...styles.actionButton,
                          backgroundColor: '#F3F4F6',
                          color: '#6B7280'
                        }}
                      >
                        {t('dismiss')}
                      </button>
                    </>
                  ) : (
                    <span style={{ fontSize: '11px', color: '#9CA3AF' }}>{t('done')}</span>
                  )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div style={styles.title}>{t('contentModeration')}</div>
        {(activeSubTab === 'articles' || activeSubTab === 'comments') && (
          <div style={styles.searchBox}>
            <Search size={16} color={theme?.text?.secondary || '#666'} />
            <input
              type="text"
              placeholder={t('searchPlaceholder', { type: activeSubTab })}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={styles.searchInput}
            />
          </div>
        )}
        <button
          onClick={() => {
            if (activeSubTab === 'articles') loadArticles();
            else if (activeSubTab === 'comments') loadComments();
            else if (activeSubTab === 'reports') loadProblemReports();
            else if (activeSubTab === 'contentReports') loadContentReports();
            else if (activeSubTab === 'chatReports') loadChatReports();
            else if (activeSubTab === 'invitationReports') setInvitationReportsReload(n => n + 1);
          }}
          disabled={loading}
          style={{
            ...styles.refreshButton,
            opacity: loading ? 0.6 : 1,
            cursor: loading ? 'default' : 'pointer'
          }}
        >
          <RefreshCw size={14} />
          {t('refresh')}
        </button>
      </div>

      <div style={styles.subTabBar}>
        <button
          onClick={() => setActiveSubTab('articles')}
          style={styles.subTab(activeSubTab === 'articles')}
        >
          {t('articlesTab')}
        </button>
        <button
          onClick={() => setActiveSubTab('comments')}
          style={styles.subTab(activeSubTab === 'comments')}
        >
          {t('commentsTab')}
        </button>
        <button
          onClick={() => setActiveSubTab('contentReports')}
          style={styles.subTab(activeSubTab === 'contentReports')}
        >
          {t('articleReportsTab')}
          {pendingContentReportCount > 0 && (
            <span style={{
              marginLeft: '6px',
              padding: '1px 6px',
              backgroundColor: '#DC2626',
              color: 'white',
              borderRadius: '10px',
              fontSize: '11px',
              fontWeight: '700'
            }}>
              {pendingContentReportCount}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveSubTab('chatReports')}
          style={styles.subTab(activeSubTab === 'chatReports')}
        >
          {t('chatReportsTab') || 'Chat/Comment Reports'}
          {pendingChatReportCount > 0 && (
            <span style={{
              marginLeft: '6px',
              padding: '1px 6px',
              backgroundColor: '#DC2626',
              color: 'white',
              borderRadius: '10px',
              fontSize: '11px',
              fontWeight: '700'
            }}>
              {pendingChatReportCount}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveSubTab('reports')}
          style={styles.subTab(activeSubTab === 'reports')}
        >
          {t('problemReportsTab')}
        </button>
        {/* English only: the Admin Panel ruling (Michael, 3 Oct 2026). Invitations Build 2 §4, #54. */}
        <button
          onClick={() => setActiveSubTab('invitationReports')}
          style={styles.subTab(activeSubTab === 'invitationReports')}
        >
          Invitation reports
        </button>
      </div>

      {activeSubTab === 'articles' && renderArticles()}
      {activeSubTab === 'comments' && renderComments()}
      {activeSubTab === 'contentReports' && renderContentReports()}
      {activeSubTab === 'chatReports' && renderChatReports()}
      {activeSubTab === 'reports' && renderProblemReports()}
      {activeSubTab === 'invitationReports' && <InvitationReportsSection reload={invitationReportsReload} />}

      <ConfirmationModal
        visible={infoModal.visible}
        message={infoModal.message}
        onOk={() => setInfoModal({ visible: false, message: '' })}
        okLabel="OK"
      />

      {/* Confirm modal — used for reversals (restore article, restore comment) */}
      <ConfirmationModal
        visible={confirmModal.visible}
        title={confirmModal.title}
        message={confirmModal.message}
        okLabel={t('confirm') || 'Confirm'}
        cancelLabel={t('cancel') || 'Cancel'}
        onOk={confirmModal.onOk || (() => {})}
        onCancel={closeConfirmModal}
      />

      {/* Article preview modal — responsive, matches ArticleDetail layout */}
      {previewArticle && (
        <div
          onClick={() => setPreviewArticle(null)}
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 10001, display: 'flex', alignItems: windowWidth < 640 ? 'stretch' : 'center', justifyContent: 'center', padding: windowWidth < 640 ? '0' : '24px 16px' }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ backgroundColor: 'white', borderRadius: windowWidth < 640 ? '0' : '12px', width: '100%', maxWidth: windowWidth < 640 ? 'none' : '768px', height: windowWidth < 640 ? '100%' : '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 32px rgba(0,0,0,0.3)', overflow: 'hidden' }}
          >
            {/* Admin header bar */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', backgroundColor: '#F9FAFB', borderBottom: '1px solid #E5E7EB', flexShrink: 0 }}>
              <span style={{ fontSize: '11px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Article Preview</span>
              <button
                onClick={() => setPreviewArticle(null)}
                style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#6B7280', lineHeight: 1, padding: '0 2px' }}
              >
                ×
              </button>
            </div>

            {windowWidth < 640 ? (
              /* Mobile: single column, full-screen scrollable */
              <div style={{ flex: 1, overflowY: 'auto', backgroundColor: '#F8FAFC' }}>
                {previewArticle.video_url ? (
                  getVideoEmbed(previewArticle.video_url) ? (
                    <div style={{ width: '100%', aspectRatio: '16 / 9', position: 'relative' }}>
                      <iframe src={getVideoEmbed(previewArticle.video_url)} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen" allowFullScreen title="Article Video" />
                    </div>
                  ) : (
                    <div style={{ background: '#FFF3CD', border: '1px solid #FFEAA7', borderRadius: '8px', padding: '12px', margin: '16px 16px 0' }}>
                      <p style={{ fontSize: '13px', color: '#856404', margin: '0 0 6px 0' }}><strong>Video link:</strong></p>
                      <a href={previewArticle.video_url} target="_blank" rel="noopener noreferrer" style={{ color: '#0066CC', fontSize: '12px', wordBreak: 'break-all' }}>{previewArticle.video_url}</a>
                    </div>
                  )
                ) : previewArticle.cover_image_url ? (
                  <div style={{ width: '66%', aspectRatio: '4 / 3', overflow: 'hidden' }}>
                    <img src={previewArticle.cover_image_url} alt={previewArticle.title} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                ) : null}
                <div style={{ padding: '16px' }}>
                  <h1 style={{ fontSize: '18px', fontWeight: '700', color: '#1F2937', margin: '0 0 12px 0', lineHeight: '1.3' }}>
                    {previewArticle.title}
                  </h1>
                  {previewArticle.summary && (
                    <p style={{ fontSize: '14px', fontWeight: '600', color: '#1F2937', lineHeight: '1.5', margin: '0 0 16px 0' }}>
                      {previewArticle.summary}
                    </p>
                  )}
                  {previewArticle.article_type === 'event' && (previewArticle.start_date || previewArticle.venue_name) && (
                    <div style={{ backgroundColor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '8px', padding: '12px', marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {previewArticle.start_date && (
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', color: '#64748B', fontSize: '14px' }}>
                          <Calendar size={16} style={{ marginTop: '2px', flexShrink: 0 }} />
                          <div>
                            <div>{new Date(previewArticle.start_date).toLocaleDateString(formattingLocale, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div>
                            <div style={{ fontSize: '13px' }}>
                              {new Date(previewArticle.start_date).toLocaleTimeString(formattingLocale, { hour: '2-digit', minute: '2-digit' })}
                              {previewArticle.end_date && ` – ${new Date(previewArticle.end_date).toLocaleTimeString(formattingLocale, { hour: '2-digit', minute: '2-digit' })}`}
                            </div>
                          </div>
                        </div>
                      )}
                      {previewArticle.venue_name && (
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', color: '#64748B', fontSize: '14px' }}>
                          <MapPin size={16} style={{ marginTop: '2px', flexShrink: 0 }} />
                          <div>
                            <div style={{ fontWeight: '500' }}>{previewArticle.venue_name}</div>
                            {previewArticle.venue_address && <div style={{ fontSize: '13px' }}>{previewArticle.venue_address}</div>}
                            {previewArticle.venue_postcode && <div style={{ fontSize: '13px' }}>{previewArticle.venue_postcode}</div>}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  <div>
                    {renderArticleContent(previewArticle.content, previewArticle.inline_images)}
                  </div>
                  <div style={{ fontSize: '13px', color: '#9CA3AF', fontStyle: 'italic', marginTop: '20px', marginBottom: '24px' }}>End</div>
                  <div style={{ borderTop: '1px solid #E5E7EB', margin: '16px 0' }} />
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontSize: '10px', color: '#6B7280', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>Author</div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: theme?.primary || '#024B99', marginBottom: '4px' }}>{previewArticle.author?.display_name || '—'}</div>
                    {previewArticle.created_at && (
                      <div style={{ fontSize: '12px', color: '#6B7280' }}>
                        {new Date(previewArticle.created_at).toLocaleDateString(formattingLocale, { day: 'numeric', month: 'long', year: 'numeric' })}
                      </div>
                    )}
                  </div>
                  <button onClick={() => setPreviewArticle(null)} style={{ width: '100%', padding: '12px', backgroundColor: theme?.primary || '#024B99', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                    {t('cancel') || 'Close'}
                  </button>
                </div>
              </div>
            ) : (
              /* Desktop/Tablet: two-column layout matching ArticleDetail */
              <>
                <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                  {/* Left sidebar — image, title, author */}
                  <div style={{ width: '45%', minWidth: '45%', borderRight: '1px solid #E5E7EB', overflowY: 'auto', padding: '20px', backgroundColor: '#F8FAFC' }}>
                    {!previewArticle.video_url && previewArticle.cover_image_url && (
                      <div style={{ width: '66%', aspectRatio: '4 / 3', overflow: 'hidden', borderRadius: '8px', marginBottom: '16px' }}>
                        <img src={previewArticle.cover_image_url} alt={previewArticle.title} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </div>
                    )}
                    <h1 style={{ fontSize: '20px', fontWeight: '700', color: '#1F2937', lineHeight: '1.3', margin: '0 0 20px 0' }}>
                      {previewArticle.title}
                    </h1>
                    <div style={{ borderTop: '1px solid #E5E7EB', paddingTop: '16px' }}>
                      <div style={{ fontSize: '10px', color: '#6B7280', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Author</div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: theme?.primary || '#024B99', marginBottom: '4px' }}>{previewArticle.author?.display_name || '—'}</div>
                      {previewArticle.created_at && (
                        <div style={{ fontSize: '12px', color: '#6B7280' }}>
                          {new Date(previewArticle.created_at).toLocaleDateString(formattingLocale, { day: 'numeric', month: 'long', year: 'numeric' })}
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Right content area — video (if any), summary, event details, body */}
                  <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
                    {previewArticle.video_url && (
                      getVideoEmbed(previewArticle.video_url) ? (
                        <div style={{ width: '100%', aspectRatio: '16 / 9', position: 'relative', marginBottom: '16px', borderRadius: '8px', overflow: 'hidden' }}>
                          <iframe src={getVideoEmbed(previewArticle.video_url)} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen" allowFullScreen title="Article Video" />
                        </div>
                      ) : (
                        <div style={{ background: '#FFF3CD', border: '1px solid #FFEAA7', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
                          <p style={{ fontSize: '13px', color: '#856404', margin: '0 0 6px 0' }}><strong>Video link:</strong></p>
                          <a href={previewArticle.video_url} target="_blank" rel="noopener noreferrer" style={{ color: '#0066CC', fontSize: '12px', wordBreak: 'break-all' }}>{previewArticle.video_url}</a>
                        </div>
                      )
                    )}
                    {previewArticle.summary && (
                      <p style={{ fontSize: '15px', fontWeight: '600', color: '#1F2937', lineHeight: '1.5', margin: '0 0 16px 0' }}>
                        {previewArticle.summary}
                      </p>
                    )}
                    {previewArticle.article_type === 'event' && (previewArticle.start_date || previewArticle.venue_name) && (
                      <div style={{ backgroundColor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '8px', padding: '12px', marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {previewArticle.start_date && (
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', color: '#64748B', fontSize: '14px' }}>
                            <Calendar size={16} style={{ marginTop: '2px', flexShrink: 0 }} />
                            <div>
                              <div>{new Date(previewArticle.start_date).toLocaleDateString(formattingLocale, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div>
                              <div style={{ fontSize: '13px' }}>
                                {new Date(previewArticle.start_date).toLocaleTimeString(formattingLocale, { hour: '2-digit', minute: '2-digit' })}
                                {previewArticle.end_date && ` – ${new Date(previewArticle.end_date).toLocaleTimeString(formattingLocale, { hour: '2-digit', minute: '2-digit' })}`}
                              </div>
                            </div>
                          </div>
                        )}
                        {previewArticle.venue_name && (
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', color: '#64748B', fontSize: '14px' }}>
                            <MapPin size={16} style={{ marginTop: '2px', flexShrink: 0 }} />
                            <div>
                              <div style={{ fontWeight: '500' }}>{previewArticle.venue_name}</div>
                              {previewArticle.venue_address && <div style={{ fontSize: '13px' }}>{previewArticle.venue_address}</div>}
                              {previewArticle.venue_postcode && <div style={{ fontSize: '13px' }}>{previewArticle.venue_postcode}</div>}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    <div>
                      {renderArticleContent(previewArticle.content, previewArticle.inline_images)}
                    </div>
                    <div style={{ fontSize: '13px', color: '#9CA3AF', fontStyle: 'italic', marginTop: '20px' }}>End</div>
                  </div>
                </div>
                {/* Footer close button */}
                <div style={{ padding: '12px 16px', borderTop: '1px solid #E5E7EB', flexShrink: 0, backgroundColor: '#F9FAFB' }}>
                  <button onClick={() => setPreviewArticle(null)} style={{ width: '100%', padding: '10px', backgroundColor: theme?.primary || '#024B99', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                    {t('cancel') || 'Close'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Reason modal — used for all destructive / moderation actions */}
      {reasonModal.visible && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 10001, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', width: '100%', maxWidth: '420px', boxShadow: '0 4px 20px rgba(0,0,0,0.25)' }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '700', color: '#1A1A1A', textTransform: 'capitalize' }}>
              {reasonModal.title}
            </h3>
            {reasonModal.subtitle && (
              <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: '#555' }}>{reasonModal.subtitle}</p>
            )}
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px', color: '#1A1A1A' }}>
              {t('reason')}
            </label>
            <textarea
              autoFocus
              value={reasonText}
              onChange={e => setReasonText(e.target.value)}
              placeholder={reasonModal.placeholder}
              rows={4}
              style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #DDD', fontSize: '13px', fontFamily: 'OpenSauceSans', resize: 'vertical', boxSizing: 'border-box' }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px' }}>
              <button
                onClick={closeReasonModal}
                style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #DDD', background: 'none', fontSize: '13px', cursor: 'pointer' }}
              >
                {t('cancel') || 'Cancel'}
              </button>
              <button
                onClick={async () => {
                  if (!reasonText.trim() || reasonSubmitting) return;
                  setReasonSubmitting(true);
                  await reasonModal.onConfirm(reasonText.trim());
                  closeReasonModal();
                  setReasonSubmitting(false);
                }}
                disabled={!reasonText.trim() || reasonSubmitting}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: !reasonText.trim() || reasonSubmitting ? '#93C5FD' : (theme?.primary || '#024B99'),
                  color: 'white',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: !reasonText.trim() || reasonSubmitting ? 'default' : 'pointer'
                }}
              >
                {reasonSubmitting ? 'Saving…' : (t('confirm') || 'Confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentModerationTab;
